import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Info, AlertTriangle, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { parseNum, dm, PERIODS } from "@/lib/format";
import { Button, Card, Field, Input, Page, PageHeader } from "@/components/kit";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const shift = (s, days) => { const d = new Date(`${s}T12:00`); d.setDate(d.getDate() + days); return iso(d); };
const HINT = { semanal: "segunda a domingo", quinzenal: "dia 1 ao 15 ou dia 16 ao fim do mês", mensal: "período mensal" };

export default function NewConference() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [day, setDay] = useState(iso(new Date()));
  const [revenue, setRevenue] = useState("");
  const { data: p } = useQuery({ queryKey: ["conf-period", day], queryFn: () => api(`/conferences/period?day=${day}`) });
  const name = PERIODS[p?.period]?.name || "Período";
  const m = useMutation({
    mutationFn: () => api("/conferences", { method: "POST", body: { week_start: p.start, revenue: parseNum(revenue) } }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["conferences"] });
      qc.invalidateQueries({ queryKey: ["summary"] });
      toast.success("Conferência criada");
      nav(`/conference/${r.id}`, { replace: true });
    },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Page narrow>
      <PageHeader title="Nova conferência" close />
      <Card className="flex flex-col gap-6">
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="label" data-testid="period-kind-label">{name} de referência</div>
            <button className="chip" onClick={() => nav("/conference-period")} data-testid="change-period-link"><Settings2 size={13} /> Alterar período</button>
          </div>
          <div className="flex items-center gap-3">
            <button className="icon-btn" disabled={!p} onClick={() => setDay(shift(p.start, -1))} data-testid="prev-week-button"><ChevronLeft size={18} /></button>
            <div className="flex-1 text-center">
              <div className="h2 tnum" data-testid="selected-week-text">{p ? `${dm(p.start)} – ${dm(p.end)}` : "…"}</div>
              <div className="caption">{p ? `${p.start.slice(0, 4)} · ${p.period === "mensal" && p.start.slice(8) === "01" ? "mês do calendário" : HINT[p.period]}` : ""}</div>
            </div>
            <button className="icon-btn" disabled={!p} onClick={() => setDay(shift(p.end, 1))} data-testid="next-week-button"><ChevronRight size={18} /></button>
          </div>
          {p?.taken && <p className="caption flex items-center gap-1.5 mt-3" style={{ color: "var(--danger)" }} data-testid="period-taken-warning"><AlertTriangle size={14} /> Já existe uma conferência neste período.</p>}
        </div>
        <Field label="Faturamento do período (R$)">
          <Input inputMode="decimal" placeholder="0,00" value={revenue} onChange={(e) => setRevenue(e.target.value)} data-testid="conference-revenue-input" />
        </Field>
        <div className="flex gap-3 rounded-xl p-3" style={{ background: "var(--brand-tint)" }}>
          <Info size={18} style={{ color: "var(--brand)" }} className="shrink-0 mt-0.5" />
          <p className="text-sm">O estoque inicial de cada produto é preenchido com o estoque final da última conferência finalizada. Você pode informar o faturamento depois.</p>
        </div>
        <Button onClick={() => m.mutate()} loading={m.isPending} disabled={!p || p.taken} data-testid="create-conference-button">Iniciar conferência</Button>
      </Card>
    </Page>
  );
}
